import axios from "axios";
import { scaleTime } from "d3";

const fetchSecureData = async (
  url,
  setData,
  dataLoaded,
  setDataLoaded,
  setError
) => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      setError("Authorization token is missing.");
      return;
    }

    if (dataLoaded) {
      return;
    }

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setData(response.data);

    setDataLoaded(true);
  } catch (error) {
    console.error("Error fetching users data:", error);
    const message =
      error.response && error.response.status === 403
        ? "You do not have the necessary permissions."
        : "Failed to fetch user data. Please try again later.";
    setError(message);
  }
};

const shadeColour = (colour, percent) => {
  const num = parseInt(colour.slice(1), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;

  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const handleImageChange = (e, data, setData) => {
  const file = e.target.files[0];

  if (!file) {
    return;
  }

  const maxSize = 2 * 1024 * 1024; // 2MB

  // Function to compress image
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const maxWidth = 800; // Max width for compressed image
          const maxHeight = 800; // Max height for compressed image
          let width = img.width;
          let height = img.height;

          // Scale down the image while maintaining aspect ratio
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Compression failed"));
              }
            },
            "image/jpeg",
            0.7 // Compression quality (0.0 - 1.0)
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
    });
  };

  // Check and compress if file is larger than maxSize
  if (file.size > maxSize) {
    compressImage(file)
      .then((compressedFile) => {
        setData({ ...data, image: compressedFile });
        alert("Image was compressed and uploaded successfully.");
      })
      .catch((err) => {
        console.error("Image compression failed:", err);
        alert("Failed to compress image. Please try again.");
      });
  } else {
    setData({ ...data, image: file });
  }
};

const hasAccess = (userRole, allowedRoles) => {
  if (!userRole) return false;

  if (Array.isArray(allowedRoles)) {
    // Check if userRole matches any role in the array
    return allowedRoles.includes(userRole);
  }

  // Single role check
  return userRole === allowedRoles;
};

const capitalizeFirstLetter = (word) => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1); // Set tomorrow's date

  // Remove time for accurate comparison
  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  if (isToday) return "Today";
  if (isTomorrow) return "Tomorrow";

  const options = {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const formattedDate = date.toLocaleDateString("en-GB", options); // Example: Thursday, 10 Nov 2024

  const day = date.getDate();
  const ordinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dayWithOrdinal = `${day}${ordinalSuffix(day)}`;

  return formattedDate.replace(`${day}`, dayWithOrdinal); // Ensures ', 10th Nov' format
};

const formatPhoneNumber = (phoneNumber) => {
  // Ensure the input is a string
  let str = phoneNumber.toString().trim();

  // Handle numbers starting with '0' and replace with '+234'
  if (str.startsWith("0")) {
    str = `+234${str.slice(1)}`;
  }

  // Handle numbers without '+234' prefix but starting with '234'
  if (str.startsWith("234")) {
    str = `+${str}`;
  }

  // Format the number to `+234 805 503 8792`
  const formattedNumber = str.replace(
    /^\+234(\d{3})(\d{3})(\d{4})$/,
    "+234 $1 $2 $3"
  );

  return formattedNumber;
};

const formatRegNumber = (regNumber) => {
  if (!regNumber) return "";

  // Remove all non-alphanumeric characters and convert to uppercase
  const cleaned = regNumber.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  // Ensure the string is at least 7 characters for valid formatting
  if (cleaned.length < 7) return regNumber.toUpperCase();

  // Extract and format components
  const part1 = cleaned.slice(0, 3); // First 3 letters
  const part2 = cleaned.slice(3, 6); // Next 3 digits
  const part3 = cleaned.slice(6); // Remaining letters

  return `${part1} ${part2} ${part3}`;
};

const formatNumber = (number) => {
  if (isNaN(number)) return "Invalid number";
  return Number(number).toLocaleString("en-US");
};

const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getSizeName = (value, sizes) => {
  const sizeObj = sizes.find((size) => size.value === value);
  return sizeObj ? sizeObj.name : "Unknown Size"; // Default fallback
};

const transformSales = (sales, products, branches) => {
  return sales.map((sale) => {
    // Find the corresponding branch
    const branch = branches.find((b) => b.branchId === sale.branchId);

    // Find the corresponding product
    const product = products.find((p) => p.productId === sale.productId);

    // Return the transformed sale object
    return {
      id: sale.id,
      saleId: sale.saleId,
      branch: branch ? branch.branchName : "Unknown Branch", // Fallback if branch not found
      customer: sale.customer,
      product: product ? product.product : "Unknown Product", // Fallback if product not found
      size: product ? product.size : "Unknown Size", // Fallback if product not found
      quantity: sale.quantity,
      saleTime: sale.saleTime,
      year: sale.year,
      month: sale.month,
    };
  });
};

const transformSalesTarget = (salesTarget, products) => {
  return salesTarget.map((target) => {
    // Find the corresponding product
    const product = products.find((p) => p.productId === target.productId);

    // Return the transformed target object
    return {
      id: target.id,
      product: product ? product.product : "Unknown Product",
      size: product ? product.size : "Unknown Size",
      target: target.target,
      year: target.year,
      month: target.month,
    };
  });
};

const transformSalesData = (data) => {
  // Ensure data is always an array
  if (!Array.isArray(data)) {
    console.warn("Invalid data format, expected an array but got:", data);
    return [
      { label: "Year", options: [] },
      { label: "Month", options: [] },
      { label: "Branch", options: [] },
      { label: "Customer", options: [] },
      { label: "Product", options: [] },
      { label: "Size", options: [] },
    ];
  }

  const fields = ["year", "month", "branch", "customer", "product", "size"];

  return fields.map((field) => ({
    label: field.charAt(0).toUpperCase() + field.slice(1),
    options: [...new Set(data.map((item) => item[field]).filter(Boolean))], // Ensure unique & non-empty values
  }));
};

const countUnique = (data, category) => {
  // Create a Set to store unique salesIds
  const uniqueList = new Set();

  // Iterate through the sales data and add each salesId to the Set
  data.forEach((datum) => {
    if (datum[category]) {
      // Check if the category exists in the object
      uniqueList.add(datum[category]);
    }
  });

  // Return the size of the Set, which represents the number of unique salesIds
  return uniqueList.size;
};

const sumCategory = (data, category, quantity) => {
  // Create an object to store the total quantity for each category
  const result = {};

  // Iterate through the data
  data.forEach((datum) => {
    // Dynamically access the category and quantity values
    const categoryValue = datum[category];
    const quantityNum = parseInt(datum[quantity], 10);

    // If the category already exists in the object, add to its quantity
    if (result[categoryValue]) {
      result[categoryValue] += quantityNum;
    } else {
      // Otherwise, initialize it with the current quantity
      result[categoryValue] = quantityNum;
    }
  });

  // Return the object with total quantities by category
  return result;
};

const getValueByKey = (data, key) => {
  // Return the value corresponding to the key, or undefined if the key doesn't exist
  return data[key];
};

const generateCalendar = (startDate) => {
  const start = new Date(startDate);
  const end = new Date(); // current date
  const calendar = [];
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const getWeekNumber = (date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - startOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
  };

  for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const dayIndex = date.getDay();
    const weekNumber = getWeekNumber(date);
    const quarter = Math.floor(monthIndex / 3) + 1;

    calendar.push({
      date: date.toISOString().split("T")[0],
      year: year,
      quarter: quarter.toString(),
      month: monthNames[monthIndex],
      week: weekNumber.toString(),
      day: dayNames[dayIndex],
    });
  }

  return calendar;
};

const sumValues = (data, property) => {
  return data.reduce((total, item) => total + item[property], 0);
};

const getTypeOptions = (products) => {
  const typeOptions = {};

  products.forEach((product) => {
    // Skip if category or type is missing
    if (!product.category || !product.type) return;

    // Initialize the category array if it doesn't exist
    if (!typeOptions[product.category]) {
      typeOptions[product.category] = [];
    }

    // Add the type if it's not already in the array
    if (!typeOptions[product.category].includes(product.type)) {
      typeOptions[product.category].push(product.type);
    }
  });

  return typeOptions;
};

export {
  fetchSecureData,
  shadeColour,
  handleImageChange,
  hasAccess,
  capitalizeFirstLetter,
  formatDate,
  formatPhoneNumber,
  formatRegNumber,
  formatNumber,
  capitalizeWords,
  getSizeName,
  transformSales,
  transformSalesTarget,
  transformSalesData,
  countUnique,
  sumCategory,
  getValueByKey,
  generateCalendar,
  sumValues,
  getTypeOptions,
};
